import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";

const UeberUnsPage = () => {
  const [about, setAbout] = useState(null);

  useEffect(() => {
    const fetchAbout = async () => {
      try {
        const res = await axios.get(`${API}/about`);
        setAbout(res.data);
      } catch (error) {
        console.error("Error fetching about:", error);
      }
    };
    fetchAbout();
  }, []);

  return (
    <div data-testid="ueber-uns-page">
      {/* Hero Section */}
      <section className="relative py-24" style={{ background: 'linear-gradient(135deg, #0A2A43 0%, #0A2A43 50%, #4a1a2a 85%, #6b1f2f 100%)' }}>
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 lg:px-16 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 font-barlow italic animate-on-load animate-fade-in-down">
            Über uns
          </h1>
          <p className="text-white/80 text-lg max-w-2xl mx-auto animate-on-load animate-fade-in-up animation-delay-300">
            Lernen Sie die Gashi Bau GmbH kennen – Ihr zuverlässiger Partner für Betonbau in der Region.
          </p>
        </div>
      </section>

      {/* About Content */}
      <section className="section-padding bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-[#D32F2F] font-semibold text-sm uppercase tracking-wider mb-2 block">
                Betonbau Meisterbetrieb
              </span>
              <h2 
                className={`font-bold mb-6 ${about?.title_size || 'text-3xl md:text-4xl'}`}
                style={{ 
                  color: about?.title_color || '#0A2A43',
                  fontFamily: about?.title_font || 'Barlow'
                }}
              >
                {about?.title || "Über uns"}
              </h2>
              <div 
                className={`leading-relaxed space-y-4 ${about?.content_size || 'text-base'}`}
                style={{ 
                  color: about?.content_color || '#475569',
                  fontFamily: about?.content_font || 'Manrope'
                }}
              >
                {about?.content?.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                )) || (
                  <p>Die Gashi Bau GmbH ist ein traditionsreiches Bauunternehmen mit Sitz in Belm. Als Betonbau Meisterbetrieb stehen wir für Qualität, Zuverlässigkeit und fachgerechte Ausführung.</p>
                )}
              </div>
            </div>
            <div className="relative">
              <img
                src={about?.image_url || "https://images.unsplash.com/photo-1632862378103-8248dccb7e3d"}
                alt="Unser Team"
                className="w-full aspect-[4/3] object-cover rounded-sm shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section-padding bg-[#F2F2F2]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0A2A43] mb-4 font-barlow">
              Unsere Werte
            </h2>
            <p className="text-[#475569] max-w-2xl mx-auto">
              Diese Grundsätze leiten uns bei jedem Projekt.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-sm shadow-md text-center" data-testid="value-qualitaet">
              <div className="w-16 h-16 bg-[#0A2A43] rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0A2A43] mb-2 font-barlow">Qualität</h3>
              <p className="text-[#475569] text-sm">Meisterqualität in jedem Detail unserer Arbeit.</p>
            </div>

            <div className="bg-white p-6 rounded-sm shadow-md text-center" data-testid="value-zuverlaessigkeit">
              <div className="w-16 h-16 bg-[#0A2A43] rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0A2A43] mb-2 font-barlow">Zuverlässigkeit</h3>
              <p className="text-[#475569] text-sm">Auf uns können Sie sich verlassen.</p>
            </div>

            <div className="bg-white p-6 rounded-sm shadow-md text-center" data-testid="value-termintreue">
              <div className="w-16 h-16 bg-[#0A2A43] rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0A2A43] mb-2 font-barlow">Termintreue</h3>
              <p className="text-[#475569] text-sm">Pünktliche Fertigstellung garantiert.</p>
            </div>

            <div className="bg-white p-6 rounded-sm shadow-md text-center" data-testid="value-kompetenz">
              <div className="w-16 h-16 bg-[#0A2A43] rounded-sm flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
              </div>
              <h3 className="text-lg font-bold text-[#0A2A43] mb-2 font-barlow">Kompetenz</h3>
              <p className="text-[#475569] text-sm">Fachkundige Beratung und Ausführung.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#0A2A43]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 lg:px-16 text-center">
          <h2 className="text-3xl font-bold text-white mb-4 font-barlow">
            Bereit für Ihr Projekt?
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Lassen Sie uns gemeinsam Ihr Bauvorhaben besprechen. Wir freuen uns auf Ihre Anfrage!
          </p>
          <Link to="/kontakt" className="bg-[#D32F2F] text-white font-semibold uppercase tracking-wider px-8 py-3 rounded-sm transition-all duration-300 hover:bg-white hover:text-[#0A2A43]" data-testid="ueber-uns-cta">
            Kontakt aufnehmen
          </Link>
        </div>
      </section>
    </div>
  );
};

export default UeberUnsPage;
